// Context + hook for the ApiErrorDispatcher.
//
// Kept in its own file (no JSX) so ApiErrorDispatcher.tsx only exports a
// component (satisfies react-refresh/only-export-components).

import { createContext, useContext } from "react";
import type { ApiError } from "./client";
import type { ErrorClass } from "./errorHandler";

/**
 * Options for a single dispatch call.
 *
 * - `actionLabel`: appended to the toast title so operators know which
 *   action failed (e.g. "Revoke" → "Revoke — role not held at site").
 * - `inlineHandler`: page-owned predicate. If it returns true for a given
 *   classification, the dispatcher suppresses its default toast so the
 *   page can render a local indicator instead (e.g. row-level chip for
 *   `role_not_held` on `/admin/grants`).
 */
export interface DispatchOptions {
  actionLabel?: string;
  inlineHandler?: (cls: ErrorClass) => boolean;
}

export type DispatchApiError = (
  err: ApiError,
  options?: DispatchOptions,
) => void;

export const ApiErrorDispatcherContext = createContext<
  DispatchApiError | undefined
>(undefined);

/**
 * Hook returning a function that centrally dispatches ApiError handling
 * per DESIGN.md §10. Components/pages call this from their catch blocks
 * instead of rolling their own toast + redirect logic.
 */
export function useApiErrorHandler(): DispatchApiError {
  const ctx = useContext(ApiErrorDispatcherContext);
  if (ctx === undefined) {
    throw new Error(
      "useApiErrorHandler must be used inside <ApiErrorDispatcher>",
    );
  }
  return ctx;
}
